import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, ArrowRight, Wrench, Truck, Broom, Toolbox, Trash, CheckCircle, Tag, Storefront, ArrowUpRight, ShoppingCart, Gift,
  Star, Quotes,
} from "@phosphor-icons/react";
import { BUSINESS, PILLARS, WHY_US, TESTIMONIALS } from "@/data/site";
import { Marquee } from "@/components/Marquee";
import { BrandsStrip } from "@/components/BrandsStrip";
import ReferChip from "@/components/ReferChip";
import RecentlyViewed from "@/components/RecentlyViewed";
import { API, api } from "@/lib/api";

const HERO_IMG = "/img/hero.webp";
const TECH_IMG = "https://images.pexels.com/photos/31725399/pexels-photo-31725399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PILLAR_ICON = { wrench: Wrench, toolbox: Toolbox, broom: Broom, trash: Trash, truck: Truck };

const Stat = ({ value, label }) => (
  <div className="flex flex-col gap-1">
    <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--ji-primary)]">
      {value}
    </span>
    <span className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)]">{label}</span>
  </div>
);

// Live testimonials — pulls approved reviews from the admin-moderated queue.
// Falls back to the curated static list ONLY when no approved reviews exist yet.
const HomeTestimonials = () => {
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/reviews");
        if (!cancelled) setLive(Array.isArray(data) ? data : []);
      } catch {
        /* silent — fallback list will render */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Prefer real approved reviews (max 6). Fill remaining slots with curated ones
  // so the section always shows at least 3 cards.
  const combined = live.length >= 3 ? live.slice(0, 6) : [
    ...live,
    ...TESTIMONIALS.slice(0, 3 - live.length).map((t) => ({
      id: `curated-${t.name}`,
      name: t.name,
      city: t.city,
      quote: t.quote,
      rating: 5,
      service_used: null,
      created_at: null,
      _curated: true,
    })),
  ];

  return (
    <section className="ji-container py-20 md:py-28" data-testid="home-testimonials">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
        <div>
          <div className="ji-overline mb-4">Word from the neighborhood</div>
          <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            The kind of service you'd recommend to your mama.
          </h2>
        </div>
        <Link
          to="/reviews"
          data-testid="home-testimonials-leave"
          className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold self-start md:self-end"
          style={{ borderRadius: 4 }}
        >
          Leave a review <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {loading && live.length === 0 ? (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ji-card p-7 h-52 animate-pulse" style={{ borderRadius: 4, background: "var(--ji-surface-alt)" }} />
          ))}
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {combined.map((t, i) => (
            <div
              key={t.id || i}
              className="ji-card p-7 flex flex-col"
              style={{ borderRadius: 4 }}
              data-testid={`testimonial-${i}`}
            >
              <Quotes size={28} weight="fill" className="text-[var(--ji-accent)]" />
              <p className="mt-4 text-base leading-relaxed text-[var(--ji-text)]">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-6 pt-5 border-t border-[var(--ji-border)] flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-[var(--ji-muted)]">
                    {t.city || t.service_used || "Southern Louisiana"}
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, k) => (
                    <Star
                      key={k}
                      size={14}
                      weight={k < (t.rating || 5) ? "fill" : "regular"}
                      className={k < (t.rating || 5) ? "text-[var(--ji-accent)]" : "text-[var(--ji-border)]"}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const FeaturedShop = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/shop/featured?limit=4`);
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const trackClick = (id) => {
    try {
      const url = `${API}/shop/products/${id}/click`;
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else api.post(`/shop/products/${id}/click`).catch(() => {});
    } catch { /* silent */ }
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="ji-container py-20 md:py-28" data-testid="home-featured-shop">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="ji-overline mb-3 inline-flex items-center gap-2"><Storefront size={12} weight="fill" /> The Joseph Industries Shop</div>
          <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Same tools we trust. <span className="text-[var(--ji-accent)] italic">Delivered to your door.</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--ji-muted)] max-w-2xl leading-relaxed">
            Appliances, replacement parts, tools, electronics, cleaning supplies &amp; furniture — hand-picked and shipped by our supplier partners.
          </p>
        </div>
        <Link to="/shop" data-testid="home-shop-view-all" className="ji-btn-outline inline-flex items-center gap-2 px-5 h-12 text-sm font-semibold whitespace-nowrap self-start md:self-auto" style={{ borderRadius: 4 }}>
          Browse the shop <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
      {loading ? (
        <div className="py-10 text-center text-[var(--ji-muted)] text-sm">Loading featured products…</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {items.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.affiliate_url}
              target="_blank"
              rel="noopener noreferrer sponsored nofollow"
              onClick={() => trackClick(p.id)}
              data-testid={`home-featured-${p.id}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.05, duration: 0.35, ease: "easeOut" }}
              className="ji-card flex flex-col overflow-hidden group hover:border-[var(--ji-accent)] transition-colors"
              style={{ borderRadius: 4 }}
            >
              <div className="relative aspect-[4/3] bg-[var(--ji-surface-alt)] overflow-hidden">
                <img
                  src={p.image_url}
                  alt={p.name}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=800&q=70"; }}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {p.badge && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[var(--ji-accent)] text-white font-bold" style={{ borderRadius: 4 }}>
                    {p.badge}
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                {p.brand && <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)]">{p.brand}</div>}
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-sm font-bold tracking-tight text-[var(--ji-text)] line-clamp-2 flex-1">
                  {p.name}
                </h3>
                <div className="mt-3 flex items-baseline justify-between border-t border-[var(--ji-border)] pt-3">
                  <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold text-[var(--ji-primary)]">${Number(p.price).toFixed(2)}</span>
                  <span className="text-xs text-[var(--ji-accent)] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Shop <ArrowUpRight size={12} weight="bold" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      )}
    </section>
  );
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <ReferChip />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="ji-container pt-12 md:pt-20 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="ji-overline mb-5 inline-flex items-center gap-2" data-testid="hero-overline">
              <Tag size={12} weight="fill" />
              Free quotes & estimates · Southern Louisiana
            </div>
            <h1
              style={{ fontFamily: "Cabinet Grotesk", lineHeight: 0.95 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[var(--ji-text)]"
              data-testid="hero-title"
            >
              Reliable. Professional.<br />
              <span className="text-[var(--ji-accent)] italic" style={{ fontFamily: "Cabinet Grotesk" }}>
                Built on quality.
              </span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-[var(--ji-muted)] max-w-xl leading-relaxed">
              Joseph Industries LLC — appliance repair &amp; installation, post-construction cleanup,
              junk removal, and courier services for homes and businesses across Southern Louisiana.
              Free quotes on every service.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-request-service"
                className="ji-btn-accent inline-flex items-center gap-2 px-6 h-14 text-base font-semibold"
                style={{ borderRadius: 4 }}
              >
                Request a Free Quote <ArrowRight size={18} weight="bold" />
              </Link>
              <a
                href={`tel:${BUSINESS.primaryPhone.tel}`}
                data-testid="hero-call-cta"
                className="ji-btn-outline inline-flex items-center gap-2 px-6 h-14 text-base font-semibold"
                style={{ borderRadius: 4 }}
              >
                <Phone size={18} weight="bold" /> {BUSINESS.primaryPhone.display}
              </a>
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="inline-flex items-center gap-2 px-5 h-14 text-sm font-semibold text-[var(--ji-primary)] hover:text-[var(--ji-accent)] transition-colors"
                style={{ borderRadius: 4 }}
              >
                <Storefront size={16} weight="duotone" /> Shop the store <ArrowRight size={14} weight="bold" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <Stat value={BUSINESS.yearsExperience} label="Years experience" />
              <Stat value="3" label="Service pillars" />
              <Stat value="$0" label="Estimates & quotes" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-[var(--ji-accent)] hidden lg:block" style={{ borderRadius: 4 }} />
            <div className="relative overflow-hidden border border-[var(--ji-border)] bg-[var(--ji-bg)]" style={{ borderRadius: 4 }}>
              <img
                src={HERO_IMG}
                alt="Joseph Industries — Quality. Reliability. Solutions."
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1200"
                height="800"
                className="w-full h-[440px] md:h-[560px] object-contain"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>

        <Marquee />
      </section>

      {/* SHOP + DISCOUNTS BANNER — headlines both savings offers at the top */}
      <section className="border-y border-[var(--ji-accent)]/40 bg-[var(--ji-surface-alt)]" data-testid="home-shop-promo">
        <div className="ji-container py-10 md:py-14">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
            <div className="ji-overline text-[var(--ji-accent)] inline-flex items-center gap-2 mb-3">
              <Tag size={12} weight="fill" /> Save on Every Job
            </div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl md:text-4xl font-bold tracking-tight">
              Two easy ways to save with Joseph Industries.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Discount 1 — Shop 10% off */}
            <div className="ji-card p-6 md:p-7 flex gap-5 hover:border-[var(--ji-accent)] transition-colors" style={{ borderRadius: 4 }} data-testid="home-promo-shop">
              <span className="inline-flex w-14 h-14 items-center justify-center bg-[var(--ji-accent)] text-white flex-shrink-0" style={{ borderRadius: 4 }}>
                <ShoppingCart size={26} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ji-accent)] font-bold">Shop discount</div>
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-xl md:text-2xl font-bold tracking-tight leading-tight">
                  Buy from our shop → save <span className="text-[var(--ji-accent)]">10% on install &amp; repair.</span>
                </h3>
                <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">
                  Any appliance, part, tool, or item you buy from us qualifies for a 10% labor discount when we install or repair it.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/shop" data-testid="home-shop-promo-shop-cta" className="ji-btn-accent inline-flex items-center justify-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                    <ShoppingCart size={14} weight="bold" /> Shop now
                  </Link>
                  <Link to="/contact" data-testid="home-shop-promo-book-cta" className="ji-btn-outline inline-flex items-center justify-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                    Book install <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Discount 2 — Referral $25 off */}
            <div className="ji-card p-6 md:p-7 flex gap-5 hover:border-[var(--ji-accent)] transition-colors" style={{ borderRadius: 4 }} data-testid="home-promo-refer">
              <span className="inline-flex w-14 h-14 items-center justify-center bg-[var(--ji-primary)] text-white flex-shrink-0" style={{ borderRadius: 4 }}>
                <Gift size={26} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ji-primary)] font-bold">Referral bonus</div>
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-xl md:text-2xl font-bold tracking-tight leading-tight">
                  Refer a friend → <span className="text-[var(--ji-accent)]">$25 off both of you.</span>
                </h3>
                <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">
                  Send a friend your personal code. When they book, they save $25 and you earn $25 credit toward your next service. Stack as many as you like.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/refer" data-testid="home-refer-promo-cta" className="ji-btn-primary inline-flex items-center justify-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                    <Gift size={14} weight="bold" /> Get my code
                  </Link>
                  <Link to="/contact" data-testid="home-refer-book-cta" className="ji-btn-outline inline-flex items-center justify-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                    Have a code? <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED SHOP — moved to top per business priority */}
      <FeaturedShop />

      <RecentlyViewed />

      {/* SERVICES */}
      <section className="ji-container py-20 md:py-28" data-testid="pillars-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="ji-overline mb-4">Professional services</div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight">
              Three services.<br />One dependable crew.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6 text-[var(--ji-muted)] text-base md:text-lg leading-relaxed">
            We diagnose first, quote up-front, and only do the work that makes sense. <strong className="text-[var(--ji-text)]">Free estimates &amp; quotes on every service.</strong>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {PILLARS.map((p) => {
            const Icon = PILLAR_ICON[p.iconKey] || Wrench;
            const guideHref = {
              "appliance-repair-install": "/guides/appliance-installation",
              "courier": "/guides/courier-logistics",
              "property-home-services": "/guides/home-preservation",
            }[p.key];
            return (
              <div
                key={p.key}
                data-testid={`pillar-${p.key}`}
                className="ji-card p-8 flex flex-col"
                style={{ borderRadius: 4 }}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex w-12 h-12 items-center justify-center text-[var(--ji-primary)] bg-[var(--ji-surface-alt)]" style={{ borderRadius: 4 }}>
                    <Icon size={24} weight="duotone" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)]">{p.overline}</span>
                </div>
                <h3 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.05 }} className="mt-6 text-2xl font-bold tracking-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--ji-accent)] font-medium tracking-tight">{p.short}</p>
                <p className="mt-4 text-[var(--ji-muted)] text-sm leading-relaxed">{p.desc}</p>

                <ul className="mt-5 space-y-2 flex-1">
                  {p.items.slice(0, 4).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-[var(--ji-text)]">
                      <CheckCircle size={14} weight="duotone" className="text-[var(--ji-accent)] mt-1 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                  {p.items.length > 4 && (
                    <li className="text-xs text-[var(--ji-muted)] pl-6">+ {p.items.length - 4} more</li>
                  )}
                </ul>

                <div className="mt-6 pt-4 border-t border-[var(--ji-border)] flex flex-wrap items-center justify-between gap-3">
                  <Link
                    to="/services"
                    data-testid={`pillar-details-${p.key}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--ji-primary)] hover:text-[var(--ji-accent)]"
                  >
                    See all details <ArrowRight size={14} weight="bold" />
                  </Link>
                  {guideHref && (
                    <Link
                      to={guideHref}
                      data-testid={`pillar-guide-${p.key}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-[var(--ji-accent)] hover:underline"
                    >
                      Read the guide →
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/services" data-testid="home-services-view-all" className="ji-btn-outline inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
            View all services <ArrowRight size={14} weight="bold" />
          </Link>
          <Link to="/contact" data-testid="home-services-quote" className="ji-btn-accent inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
            Request a free quote <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>

      {/* WHY US + Image */}
      <section className="bg-[var(--ji-surface-alt)] border-y border-[var(--ji-border)]">
        <div className="ji-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overflow-hidden border border-[var(--ji-border)] ji-noise" style={{ borderRadius: 4 }}>
              <img src={TECH_IMG} alt="Technician on a service call" loading="lazy" decoding="async" className="w-full h-[420px] md:h-[520px] object-cover" data-testid="why-us-image" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="ji-overline mb-4">Why southern Louisiana trusts us</div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight">
              Built on trust, not commission.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY_US.map((w, i) => (
                <div key={w.title} className="ji-card p-5" style={{ borderRadius: 4 }} data-testid={`why-us-${i}`}>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={18} weight="duotone" className="text-[var(--ji-accent)] mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-base font-bold tracking-tight">{w.title}</h3>
                      <p className="mt-1 text-sm text-[var(--ji-muted)] leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <BrandsStrip />

      {/* TESTIMONIALS */}
      <HomeTestimonials />

      {/* CTA */}
      <section className="ji-container pb-24">
        <div className="relative overflow-hidden ji-noise" style={{ background: "var(--ji-primary)", borderRadius: 4 }}>
          <div className="relative z-10 p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#D97757] mb-3 inline-flex items-center gap-2">
                <Tag size={12} weight="fill" /> Free estimates · free quotes
              </div>
              <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl md:text-5xl font-bold tracking-tight text-white">
                Got a job for us?<br /> Let's quote it today — on the house.
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/contact" data-testid="cta-request-service" className="ji-btn-accent inline-flex items-center justify-center gap-2 h-14 px-6 text-base font-semibold" style={{ borderRadius: 4 }}>
                Get a Free Quote <ArrowRight size={18} weight="bold" />
              </Link>
              <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="cta-call-cta" className="inline-flex items-center justify-center gap-2 h-14 px-6 text-base font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors" style={{ borderRadius: 4 }}>
                <Phone size={18} weight="bold" /> Call {BUSINESS.primaryPhone.display}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
